<template>
    <form class="comment-form" :class="parentId ? 'comment-form-reply' : 'bg-gray p-4'" novalidate @submit.prevent="submit">
        <h3 class="comment-form-title">{{ replyTo ? 'Reageer op ' + replyTo : 'Reageer' }}</h3>
        <p v-if="store.staffName && !parentId" class="comment-form-staff">
            Je bent ingelogd in Atom. Je reactie krijgt het label <strong>GoedGebruik</strong>.
        </p>

        <div class="form-floating mb-3">
            <input :id="id + '-name'" ref="nameInput" v-model="form.name" type="text" class="form-control" :class="{'is-invalid': errors.name}"
                   placeholder="Je naam" maxlength="100" autocomplete="name" @input="store.rememberedName.value = form.name">
            <label :for="id + '-name'">Naam</label>
            <div v-if="errors.name" class="invalid-feedback">{{ errors.name }}</div>
        </div>

        <div class="form-floating mb-3">
            <textarea :id="id + '-body'" ref="bodyInput" v-model="form.body" class="form-control comment-textarea" :class="{'is-invalid': errors.body}"
                      placeholder="Je reactie" maxlength="2000"></textarea>
            <label :for="id + '-body'">Reactie</label>
            <div v-if="errors.body" class="invalid-feedback">{{ errors.body }}</div>
        </div>

        <!-- Honeypot: mensen zien dit veld niet, spambots vullen het in. -->
        <div class="comment-form-website" aria-hidden="true">
            <label :for="id + '-website'">Website</label>
            <input :id="id + '-website'" v-model="form.website" type="text" tabindex="-1" autocomplete="off">
        </div>

        <p class="comment-form-privacy">
            Je naam en reactie zijn voor iedereen zichtbaar. Meer hierover in onze <a href="/privacyverklaring">privacyverklaring</a>.
            <template v-if="!store.staffName">
                Dit formulier is beveiligd met reCAPTCHA; het <a href="https://policies.google.com/privacy" target="_blank" rel="noopener">privacybeleid</a>
                en de <a href="https://policies.google.com/terms" target="_blank" rel="noopener">voorwaarden</a> van Google zijn van toepassing.
            </template>
        </p>

        <div v-if="message" class="alert" :class="success ? 'alert-success' : 'alert-danger'" role="status">{{ message }}</div>

        <button type="submit" class="btn btn-primary mb-0" :class="{'btn-sm': parentId}" :disabled="busy">
            <span v-if="busy" class="spinner-border spinner-border-sm" role="status"><span class="visually-hidden">Bezig met plaatsen...</span></span>
            <template v-else>Plaatsen</template>
        </button>
        <button v-if="parentId" type="button" class="btn btn-secondary btn-sm mb-0 ms-2" :disabled="busy" @click="emit('cancel')">Annuleren</button>
    </form>
</template>

<script setup>
import {inject, onMounted, reactive, ref} from 'vue';
import {api, describeError} from './api.js';

const props = defineProps({
    // Leeg voor een reactie op het item zelf, anders het id van de reactie waarop je antwoordt.
    parentId: {type: Number, default: null},
    replyTo:  {type: String, default: null}
});

const emit = defineEmits(['created', 'cancel']);

const store = inject('comments');
const id    = 'comment-' + (props.parentId ?? 'new');

const form      = reactive({name: store.rememberedName.value, body: '', website: ''});
const errors    = ref({});
const message   = ref('');
const success   = ref(false);
const busy      = ref(false);
const nameInput = ref(null);
const bodyInput = ref(null);

// Een antwoordformulier opent met de cursor waar je moet beginnen met typen.
onMounted(() => props.parentId && (form.name ? bodyInput : nameInput).value.focus({preventScroll: true}));

// Wie in Atom is ingelogd, hoeft geen reCAPTCHA (zie Store). Lukt reCAPTCHA niet (geblokkeerd, traag),
// dan versturen we zonder token: de server meldt dat dan, samen met de andere fouten in het formulier.
const getRecaptchaToken = () => new Promise((resolve) => {
    if(store.staffName || !window.grecaptcha) {
        return resolve(null);
    }

    const timeout = setTimeout(() => resolve(null), 10000);
    const finish  = (token) => {
        clearTimeout(timeout);
        resolve(token);
    };

    window.grecaptcha.ready(() => window.grecaptcha.execute(store.recaptchaKey, {action: 'comment'}).then(finish, () => finish(null)));
});

const submit = async () => {
    busy.value    = true;
    errors.value  = {};
    message.value = '';

    try {
        const token    = await getRecaptchaToken();
        const response = await api.post(store.endpoint, {...form, parent_id: props.parentId, 'g-recaptcha-response': token});

        store.addComment(response.data.comment);
        emit('created');
        form.body     = '';
        success.value = true;
        message.value = 'Bedankt voor je reactie!';
    } catch(error) {
        const described = describeError(error);

        errors.value  = described.fields;
        success.value = false;
        message.value = described.message;
    } finally {
        busy.value = false;
    }
};
</script>
